import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../store/authStore';
import Button from '../components/Button';
import Card from '../components/Card';
import SectionHeading from '../components/SectionHeading';
import UseCaseGrid from '../components/UseCaseGrid';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { api } from '../lib/api';
import { buildSignUpUrl } from '../lib/cognitoUrls';

interface Photo {
  id: string;
  s3Key: string;
  order: number;
  isPrimary: boolean;
}

interface Listing {
  _id: string;
  name: string;
  title: string;
  description: string;
  photos: Photo[];
}

const HomePage: React.FC = () => {
  const auth = useAuth();
  const { user } = useAuthStore();
  const isAuthenticated = auth.isAuthenticated;
  const [topListings, setTopListings] = useState<Listing[]>([]);
  const [isLoadingListings, setIsLoadingListings] = useState(false);

  useEffect(() => {
    fetchTopListings();
  }, []);

  const fetchTopListings = async () => {
    setIsLoadingListings(true);
    try {
      const data = await api.get<{ listings: Listing[] }>('/listings/active');
      setTopListings(data.listings.slice(0, 6));
    } catch {
      // Silently fail - top listings is not critical
    } finally {
      setIsLoadingListings(false);
    }
  };

  const handleSignUp = () => {
    window.location.href = buildSignUpUrl();
  };

  return (
    <>
      <Helmet>
        <title>Pay2Rent.me - Peer-to-Peer Rental Platform</title>
        <meta
          name="description"
          content="A self-serve peer-to-peer rental platform where owners can list items for rent and renters can schedule rentals and make payments."
        />
      </Helmet>
      <div className="space-y-16">
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-50 via-white to-accent-50 px-6 py-20 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Renting made simple
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            A self-serve peer-to-peer rental platform where owners can list items for rent
            and renters can schedule rentals and make payments.
          </p>
          <div className="flex justify-center gap-4">
            {isAuthenticated ? (
              <Link to={user?.isOwner ? '/my-listings' : '/my-rentals'}>
                <Button size="lg">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Button size="lg" onClick={handleSignUp}>
                  Get Started
                </Button>
                <Button size="lg" variant="secondary" onClick={() => auth.signinRedirect()}>
                  Sign In
                </Button>
              </>
            )}
          </div>
          {!user?.isOwner && (
            <p className="hidden sm:block absolute bottom-4 right-6 text-sm text-gray-600">
              Have a product or service?{' '}
              <Link
                to="/become-owner"
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Become an owner
              </Link>{' '}
              and start earning today.
            </p>
          )}
        </section>

        <section>
          <SectionHeading title="How It Works" />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <Card className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">List Your Items</h3>
              <p className="text-gray-600">
                Create listings for items you want to rent out. Set your own schedule and pricing.
              </p>
            </Card>
            <Card className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Accept Bookings</h3>
              <p className="text-gray-600">
                Renters can view your availability and book time slots that work for them.
              </p>
            </Card>
            <Card className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Paid</h3>
              <p className="text-gray-600">
                Payments are processed securely through Stripe. Money lands in your bank automatically.
              </p>
            </Card>
          </div>
        </section>

        {topListings.length > 0 && (
          <section>
            <SectionHeading title="Featured Listings" />
            {isLoadingListings ? (
              <div className="text-center py-8 text-gray-600">Loading...</div>
            ) : (
              <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topListings.map(listing => {
                  const primaryPhoto =
                    listing.photos?.find(p => p.isPrimary) || listing.photos?.[0];
                  const s3BaseUrl =
                    process.env.REACT_APP_S3_BASE_URL ||
                    'https://pay2rentme-images-dev.s3.amazonaws.com';

                  return (
                    <Link key={listing._id} to={`/${listing.name}`}>
                      <Card interactive padding="none" className="overflow-hidden h-full">
                        {primaryPhoto ? (
                          <div className="h-48 bg-gray-200 overflow-hidden">
                            <img
                              src={`${s3BaseUrl}/${primaryPhoto.s3Key}`}
                              alt={listing.title}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="h-48 bg-gray-200 flex items-center justify-center">
                            <svg
                              className="w-16 h-16 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {listing.title}
                          </h3>
                          <div className="text-gray-600 text-sm line-clamp-2">
                            <MarkdownRenderer content={listing.description} />
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <section>
          <SectionHeading title="Rent out almost anything" />
          <div className="mt-10">
            <UseCaseGrid />
          </div>
        </section>

        <section>
          <Card padding="lg" className="text-center">
            <SectionHeading
              eyebrow="Simple setup"
              title="Start earning in minutes"
              subtitle="List goods or services, set your price, and money hits your bank when people book."
            />
            <div className="mt-8">
              <Link to="/become-owner">
                <Button size="lg">Become an Owner</Button>
              </Link>
            </div>
          </Card>
        </section>

        <section>
          <SectionHeading title="Why Pay2Rent.me?" />
          <div className="mt-10 grid md:grid-cols-2 gap-8">
            <Card>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Owners</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Easy listing management with flexible scheduling
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Set your own prices and availability
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Unique URL and QR code for each listing
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Secure payment processing
                </li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Renters</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Browse available items and time slots
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Book instantly with secure payment
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Flexible cancellation policies
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">✓</span>
                  Contact information provided after booking
                </li>
              </ul>
            </Card>
          </div>
        </section>

        <section className="bg-primary-600 text-white rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to start renting?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join Pay2Rent.me today and make renting simple.
          </p>
          {!isAuthenticated && (
            <Button size="lg" variant="secondary" onClick={() => auth.signinRedirect()}>
              Create Your Account
            </Button>
          )}
        </section>
      </div>
    </>
  );
};

export default HomePage;
